// src/context/NotificationContext.jsx
import React, { createContext, useContext, useState, useCallback } from 'react';
import { CheckCircle2, AlertTriangle, AlertCircle, Info, X } from 'lucide-react';
import { PALETTE } from '../lib/theme';

const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);

    const notify = useCallback((message, type = 'info') => {
        const id = Date.now();
        setToasts(prev => [...prev, { id, message, type }]);

        // Auto-remove after 4 seconds
        setTimeout(() => {
            setToasts(prev => prev.filter(t => t.id !== id));
        }, 4000);
    }, []);

    const removeToast = (id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
    };

    return (
        <NotificationContext.Provider value={notify}>
            {children}
            {/* Toast Container */}
            <div className="fixed bottom-6 right-6 z-[9999] flex flex-col gap-3 pointer-events-none">
                {toasts.map(toast => {
                    let bgClass, Icon, textColor;
                    switch (toast.type) {
                        case 'success':
                            bgClass = 'bg-emerald-500/10 border-emerald-500/20';
                            textColor = 'text-emerald-500';
                            Icon = CheckCircle2;
                            break;
                        case 'error':
                            bgClass = 'bg-rose-500/10 border-rose-500/20';
                            textColor = 'text-rose-500';
                            Icon = AlertCircle;
                            break;
                        case 'warning':
                            bgClass = 'bg-amber-500/10 border-amber-500/20';
                            textColor = 'text-amber-500';
                            Icon = AlertTriangle;
                            break;
                        default:
                            bgClass = 'bg-blue-500/10 border-blue-500/20';
                            textColor = 'text-blue-500';
                            Icon = Info;
                    }

                    return (
                        <div key={toast.id} className={`pointer-events-auto flex items-start gap-3 p-4 rounded-2xl border shadow-lg backdrop-blur-md animate-in slide-in-from-right-8 fade-in duration-300 ${bgClass} bg-[#1c1c1e] dark:bg-[#1c1c1e]`}>
                            <Icon size={20} className={`shrink-0 mt-0.5 ${textColor}`} />
                            <div className="flex-1 min-w-[200px]">
                                <p className={`text-sm font-bold ${textColor}`}>{toast.message}</p>
                            </div>
                            <button onClick={() => removeToast(toast.id)} className={`shrink-0 opacity-50 hover:opacity-100 transition-opacity ${textColor}`}>
                                <X size={16} />
                            </button>
                        </div>
                    );
                })}
            </div>
        </NotificationContext.Provider>
    );
};

export const useNotify = () => {
    const context = useContext(NotificationContext);
    if (!context) throw new Error("useNotify must be used within a NotificationProvider");
    return context;
};